import React, { useRef, useEffect, useState } from 'react';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';

import { useMsal, useIsAuthenticated, useAccount } from "@azure/msal-react";
import axios from 'axios';
import authConfig from '../../authConfig';

// Props:
// - trainingId : GUID of the MemberTraining record (used to request a token)
// - fileId : fileId / folder name under trainingfiles (used to build HLS URL)
// - isHls (optional, default true) : whether to request HLS (.m3u8) playback
const VideoPlayer = ({ trainingId, fileId, isHls = true }) => {
    const playerRef = useRef(null);
    const vjsContainerRef = useRef(null);
    const { instance, accounts } = useMsal();
    const isMsalAuthenticated = useIsAuthenticated();
    const account = useAccount(accounts[0] || {});

    const [options, setOptions] = useState({
        autoplay: true,
        controls: true,
        responsive: true,
        fluid: true,
        sources: []
    });

    // Acquire MSAL access token silently if available
    const acquireMsalToken = async () => {
        if (!isMsalAuthenticated) return null;
        try {
            const resp = await instance.acquireTokenSilent({
                scopes: authConfig.api.scopes,
                account: account
            });
            return resp?.accessToken ?? null;
        } catch (err) {
            console.warn('MSAL silent token acquisition failed', err);
            return null;
        }
    };

    useEffect(() => {
        let cancelled = false;

        const setupPlayback = async () => {
            // Require trainingId and fileId to request token and construct playback url
            if (!trainingId || !fileId) {
                console.warn('VideoPlayer requires both trainingId and fileId');
                return;
            }

            // Determine which token endpoint to call based on authentication method
            const useMsal = isMsalAuthenticated;
            const tokenEndpoint = useMsal ? authConfig.api.urls.getMemberTrainingVideoPlaybackToken : authConfig.api.urls.getMemberTrainingVideoPlaybackTokenForMember;

            let bearer = null;
            if (useMsal) {
                bearer = await acquireMsalToken();
            }

            try {
                const axiosConfig = {};
                let tokenResp;
                if (useMsal) {
                    axiosConfig.headers = bearer ? { Authorization: `Bearer ${bearer}` } : {};
                    tokenResp = await axios.post(tokenEndpoint, { memberTrainingId: trainingId }, axiosConfig);
                } else {
                    // Cookie-based member auth: include credentials
                    axiosConfig.withCredentials = true;
                    tokenResp = await axios.post(tokenEndpoint, { memberTrainingId: trainingId }, axiosConfig);
                }

                const playbackToken = tokenResp?.data;
                if (!playbackToken) {
                    console.error('No playback token returned');
                    return;
                }

                // Build playback URL. Prefer HLS (m3u8) when isHls true.
                if (isHls) {
                    const base = useMsal ? authConfig.api.urls.getMemberTrainingHlsVideo : authConfig.api.urls.getMemberTrainingHlsVideoForMember;
                    // endpoints are like: /api/membertraining/hls/{token}/{fileId}/{*filename}
                    // we'll request playlist named "playlist.m3u8"
                    const playlistUrl = `${base}/${encodeURIComponent(playbackToken)}/${encodeURIComponent(fileId)}/playlist.m3u8`;
                    if (!cancelled) {
                        setOptions(prev => ({
                            ...prev,
                            sources: [{ src: playlistUrl, type: 'application/vnd.apple.mpegurl' }],
                            // store token / bearer for xhrSetup below
                            _playbackToken: playbackToken,
                            _isMsal: useMsal,
                            _bearer: bearer
                        }));
                    }
                } else {
                    // Fallback to mp4 endpoint
                    const base = useMsal ? authConfig.api.urls.getMemberTrainingVideo : authConfig.api.urls.getMemberTrainingVideoForMember;
                    // Expect file is named by fileId (or ContentFileName). We'll try fileId.mp4
                    const filename = `${fileId}.mp4`;
                    const mp4Url = `${base}/${encodeURIComponent(filename)}`;
                    if (!cancelled) {
                        setOptions(prev => ({
                            ...prev,
                            sources: [{ src: mp4Url, type: 'video/mp4' }],
                            _playbackToken: playbackToken,
                            _isMsal: useMsal,
                            _bearer: bearer
                        }));
                    }
                }

            } catch (err) {
                console.error('Failed to acquire playback token or build URL', err);
            }
        };

        setupPlayback();

        return () => { cancelled = true; };
    }, [trainingId, fileId, isMsalAuthenticated, instance, accounts, isHls]);

    //const handlePlayerReady = (player) => {
    //    playerRef.current = player;

    //    // Attach XHR/fetch setup to include auth when player fetches segments
    //    const bearer = options._bearer || null;
    //    const isMsal = options._isMsal || false;

    //    try {
    //        player.ready(() => {
    //            // set source again to ensure proper loading
    //            if (options.sources && options.sources.length) {
    //                player.src(options.sources);
    //            }

    //            // Minimal targeted interceptor to attach auth for segment requests
    //            const originalFetch = window.fetch;
    //            window.fetch = async (input, init = {}) => {
    //                try {
    //                    const url = (typeof input === 'string') ? input : input.url || '';
    //                    const isOurApi = url.includes('/api/membertraining/');
    //                    if (isOurApi) {
    //                        if (isMsal && bearer) {
    //                            init = init || {};
    //                            init.headers = { ...(init.headers || {}), Authorization: `Bearer ${bearer}` };
    //                            init.credentials = 'omit';
    //                        } else {
    //                            init = init || {};
    //                            init.credentials = 'include';
    //                        }
    //                    }
    //                } catch (e) {
    //                    // ignore
    //                }
    //                return originalFetch(input, init);
    //            };

    //        });
    //    } catch (e) {
    //        console.warn('Error attaching fetch interceptor for video auth', e);
    //    }

    //    player.on('waiting', () => { });
    //    player.on('dispose', () => { });
    //};

    // Build options passed to VideoJS. Include xhrSetup that sets Authorization or credentials where possible.
    const videoJsOptions = {
        ...options,
        //xhrSetup: function (xhr) {
        //    // Called for XHR requests issued by Video.js (older XHR-based behavior)
        //    try {
        //        const bearer = options._bearer;
        //        const isMsal = options._isMsal;
        //        if (isMsal && bearer) {
        //            xhr.setRequestHeader('Authorization', `Bearer ${bearer}`);
        //        } else {
        //            xhr.withCredentials = true;
        //        }
        //    } catch (e) {
        //        // ignore
        //    }
        //},
        //fetchSetup: function (fetchOptions) {
        //    // Not all versions call this; left for compatibility
        //    try {
        //        const bearer = options._bearer;
        //        const isMsal = options._isMsal;
        //        if (isMsal && bearer) {
        //            fetchOptions.headers = { ...(fetchOptions.headers || {}), Authorization: `Bearer ${bearer}` };
        //            fetchOptions.credentials = 'omit';
        //        } else {
        //            fetchOptions.credentials = 'include';
        //        }
        //    } catch (e) { }
        //}
    };

    // Initialize or update video.js player when options change
    useEffect(() => {
        if (!vjsContainerRef.current) return;

        let player = playerRef.current;

        if (!player) {
            const videoElement = document.createElement("video-js");
            videoElement.classList.add('vjs-big-play-centered');
            vjsContainerRef.current.appendChild(videoElement);

            player = playerRef.current = videojs(videoElement, videoJsOptions, () => {
                videojs.log('player is ready');
                //try { handlePlayerReady(player); } catch (e) { console.warn(e); }
            });
        } else {
            // update existing player with new sources or autoplay
            try {
                player.autoplay(videoJsOptions.autoplay);
                if (videoJsOptions.sources) player.src(videoJsOptions.sources);
            } catch (e) {
                // ignore
            }
        }

        return () => {
            if (player && !player.isDisposed()) {
                player.dispose();
                playerRef.current = null;
            }
        };
    }, [options]);

    return (
        <>
            <div data-vjs-player>
                <div ref={vjsContainerRef} />
            </div>
        </>
    );
};

export default VideoPlayer;
